// src/components/applications/AwardedStudentView.js
import React, { useState, useContext, useEffect, memo } from "react";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";

import {
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";

import { NotificationContext } from "../../context/NotificationContext";
import { SettingsContext } from "../../context/SettingsContext";

import {
  useGetStudentAwardsStatusQuery,
  useClaimAwardMutation,
  useLazyViewFileQuery,
  useLazyDownloadFileQuery,
  useDeleteFileMutation,
  useUpdateStudentEmailMutation,
} from "../../services/students";
import nlsfLogo from "../../assets/icons/nlsf-scholarships.png";

import ClaimAwardDialog from "./student-awards/ClaimAwardDialog";
import ConfirmationDialog from "../helpers/ConfirmationDialog";
import { canStudentsViewAwards } from "./awardVisibility";

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";

  // Handle ISO strings with time like "2027-09-30T00:00:00.000Z"
  const plainDate = dateStr.split("T")[0];

  const [year, month, day] = plainDate.split("-").map(Number);

  // Basic sanity check
  if (!year || !month || !day) return "Invalid Date";

  const date = new Date(year, month - 1, day);

  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const AwardedStudentView = ({ isAdminView = false }) => {
  // Fix: Add missing handleOpenDialog for claim dialog
  const handleOpenDialog = (award) => {
    setSelectedAward(award);
    setOpenDialog(true);
  };
  const { settings } = useContext(SettingsContext);
  const canViewAwards = isAdminView || canStudentsViewAwards(settings);
  const { data, error, isLoading, refetch } = useGetStudentAwardsStatusQuery(
    undefined,
    {
      skip: !canViewAwards,
    }
  );
  const [claimAward, { isLoading: isClaiming }] = useClaimAwardMutation();

  const [deleteFile, { isLoading: isDeleting }] = useDeleteFileMutation();

  const [triggerView, { isFetching: isViewing }] = useLazyViewFileQuery();
  const [triggerDownload, { isFetching: isDownloading }] =
    useLazyDownloadFileQuery();

  const { addNotification } = useContext(NotificationContext);

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAward, setSelectedAward] = useState(null);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [email, setEmail] = useState(data?.data?.[0]?.email || "");
  const [emailChanged, setEmailChanged] = useState(false);
  const [showEmailEditor, setShowEmailEditor] = useState(false);

  const [updateEmail, { isLoading: isUpdatingEmail }] =
    useUpdateStudentEmailMutation();
  const emailIsLearn68 = email.toLowerCase().includes("learn68");

  useEffect(() => {
    const currentEmail = data?.data?.[0]?.email || "";
    if (currentEmail && !emailChanged) {
      setEmail(currentEmail);
    }
  }, [data, emailChanged]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailChanged(true);
  };

  const handleSaveEmail = async () => {
    try {
      const studentId = awards[0]?.student_id;
      await updateEmail({ studentId, email }).unwrap();
      addNotification("Email updated successfully", "success");
      setEmailChanged(false);
      setShowEmailEditor(false);
    } catch (err) {
      addNotification("Failed to update email", "error");
    }
  };

  if (!canViewAwards) {
    return (
      <Box textAlign="center" mt={2}>
        <Alert severity="info">
          Award results will be available on the award claim start date.
        </Alert>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box textAlign="center" mt={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" mt={2}>
        <Alert severity="error">Error loading your awards.</Alert>
      </Box>
    );
  }

  const awards = data?.data || [];

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAward(null);
  };

  const handleConfirmClaim = async ({ documentLink, files }) => {
    if (!documentLink && !files && files?.length === 0) {
      addNotification("Document/File(s) are required .", "error");
      return;
    }

    const response = await claimAward({
      awardId: selectedAward?.id,
      documentLink,
      files,
    }).unwrap();
    if (response?.success) {
      addNotification(
        response?.message || "Award claimed successfully.",
        "success"
      );
      setTimeout(() => {
        refetch?.();
        handleCloseDialog();
      }, 1000);
    } else {
      console.error("Error claiming award:", response?.message);
      alert(response?.message || "Error claiming award");
    }
  };

  const handleViewFile = async (fileId) => {
    try {
      const result = await triggerView(fileId).unwrap();
      if (result && result.success && result.url) {
        window.open(result.url, "_blank");
      } else {
        addNotification("Error retrieving file URL", "error");
      }
    } catch (error) {
      console.error("Error in handleViewFile:", error);
      addNotification("Error retrieving file URL", "error");
    }
  };

  const handleDownloadFile = async (fileId) => {
    try {
      const result = await triggerDownload(fileId).unwrap();
      if (result && result.success && result.url) {
        const link = document.createElement("a");
        link.href = result.url;
        link.setAttribute("download", "");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        addNotification("Error retrieving download URL", "error");
      }
    } catch (error) {
      console.error("Error in handleDownloadFile:", error);
      addNotification("Error retrieving download URL", "error");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete?.id) {
      addNotification("File ID not found", "error");
      return;
    }
    const result = await deleteFile(fileToDelete?.id).unwrap();
    // console.log("Delete file result:", result);
    if (result && result?.success) {
      addNotification(
        result?.message || `File deleted successfully!`,
        "success"
      );

      setTimeout(() => {
        setOpenConfirmDelete(false);
        refetch?.();
      }, 1000);
    } else {
      console.error("Error deleting file:", result);
      addNotification(result?.message || "Error deleting file", "error");
    }
  };

  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setOpenConfirmDelete(true);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ maxWidth: "900px", mx: "auto" }}>
        <CardMedia
          component="img"
          image={nlsfLogo}
          alt="Award Header Image"
          sx={{ width: "100%", height: 130, objectFit: "contain", mb: 3 }}
        />
        <Box display="flex" justifyContent="center" mb={3}>
          <Typography variant="h4" textAlign="center">
            Congratulations
          </Typography>
        </Box>

        <Typography
          variant="body2"
          textAlign="center"
          mb={3}
          sx={{ maxWidth: "100%", mx: "auto" }}
        >
          The awards you and your fellow graduates have just won have been
          provided through the generosity of community members who believe in
          education. Please be sure to send a gracious thank-you letter as our
          donors take a personal interest in the recipients and their future
          plans for post-secondary.
        </Typography>

        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            mb: 3,
            textDecoration: "underline",
            fontWeight: "900",
          }}
        >
          Procedure For Release Of Funds
        </Typography>

        <Typography variant="body2" textAlign="center" mb={3}>
          Provide proof of registration (timetable, letter of
          registration/enrolment or tuition statement – must clearly show both
          your name and the name of the post-secondary institution you are
          attending) by clicking the claim button below.
        </Typography>

        <Box
          sx={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeeba",
            borderRadius: "4px",
            p: 2,
            mb: 3,
            maxWidth: 600,
            mx: "auto",
          }}
        >
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            💡 Important:
          </Typography>

          {emailIsLearn68 ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                If your award is paid via e-transfer, it will be sent to the
                email address we have on file. Since this is a school-issued
                address, please update it. School accounts may expire before
                funds are issued.
              </Typography>

              <Typography variant="body2" sx={{ mb: 2 }}>
                You must use a personal email account (e.g., Gmail, Outlook) to
                receive the funds.
              </Typography>

              <Box
                sx={{
                  mt: 2,
                  backgroundColor: "#f8d7da",
                  border: "1px solid #f5c6cb",
                  borderRadius: 1,
                  p: 1.5,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "#721c24", fontWeight: 500 }}
                >
                  Your current email is a school-issued address and <br />
                  <strong>cannot be used</strong> for receiving award funds.
                </Typography>
              </Box>
            </>
          ) : (
            <Typography variant="body2" sx={{ mb: 2, textAlign: "center" }}>
              Funds paid by e-transfer will be sent to{" "}
              <strong>{email || "the email address we have on file"}</strong>.
            </Typography>
          )}

          {!showEmailEditor ? (
            <Box sx={{ textAlign: "center" }}>
              {emailIsLearn68 && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Current Email:</strong> {email}
                </Typography>
              )}
              <Button
                variant="outlined"
                onClick={() => setShowEmailEditor(true)}
              >
                Change Email
              </Button>
            </Box>
          ) : (
            <Box textAlign="center">
              <TextField
                label="Your Email Address"
                value={email}
                onChange={handleEmailChange}
                variant="outlined"
                size="small"
                fullWidth
                sx={{ backgroundColor: "#fff", maxWidth: 400, mb: 1 }}
                helperText={
                  emailIsLearn68
                    ? "Please update your email address — school accounts cannot receive award funds."
                    : emailChanged
                    ? "Don't forget to save your new email."
                    : ""
                }
                error={emailIsLearn68}
              />

              <Box display="flex" justifyContent="center" gap={1}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleSaveEmail}
                  disabled={isUpdatingEmail || !emailChanged}
                >
                  {isUpdatingEmail ? "Saving..." : "Save Email"}
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    setShowEmailEditor(false);
                    setEmail(data?.data?.[0]?.email || ""); // Reset email to original
                    setEmailChanged(false);
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            mb: 3,
            fontStyle: "italic",
            fontWeight: "900",
          }}
        >
          Letter Of Acceptance Is Insufficient Proof. Phone Screenshots Will Not
          Be Accepted.
        </Typography>

        {awards?.map((award) => (
          <Card
            key={award?.id}
            variant="outlined"
            sx={{ mb: 2, backgroundColor: "#f9f9f9" }}
          >
            <CardContent sx={{ px: 3, py: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  pb: 1,
                  borderBottom: "1px solid #ddd",
                  textTransform: "capitalize",
                }}
              >
                {award?.scholarship_name || "Award"}
              </Typography>

              {award?.award_date && (
                <Typography variant="body2" color="text.secondary">
                  Awarded On: {formatDate(award?.award_date)}
                </Typography>
              )}
              {award?.expiry_date && (
                <Typography variant="body2" color="text.secondary">
                  Expires On: {formatDate(award?.expiry_date)}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {award?.claim_date
                  ? `Claimed On: ${formatDate(award?.claim_date)}`
                  : "Not Claimed Yet"}
              </Typography>

              {award?.claim_date && (
                <Box
                  sx={{
                    backgroundColor: "#e8f5e9",
                    color: "#2e7d32",
                    fontSize: "0.9rem",
                    p: 1,
                    mt: 1,
                    borderRadius: 1,
                    fontStyle: "italic",
                  }}
                >
                  ✅ Your claim has been submitted and will be funded within 2
                  weeks.
                </Box>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Status:{" "}
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    px: 1,
                    py: "2px",
                    backgroundColor: "#eeeeee",
                    borderRadius: "4px",
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    ml: 1,
                    textTransform: "capitalize",
                  }}
                >
                  {award?.status}
                </Box>
              </Typography>

              {award?.status.toLowerCase() === "rejected" &&
                award?.rejection_note && (
                  <Typography
                    variant="body2"
                    color="error"
                    sx={{ mt: 1, fontStyle: "italic" }}
                  >
                    Rejection Note: {award.rejection_note}
                  </Typography>
                )}

              {award?.status.toLowerCase() === "approved" &&
                award?.approval_date && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Approved On: {formatDate(award?.approval_date)}
                  </Typography>
                )}

              {award?.files?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Attached Files:
                  </Typography>
                  {award?.files.map((file, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                        paddingLeft: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {file?.file_name} ({file?.file_type},{" "}
                        {(file?.file_size / 1024).toFixed(2)} KB)
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          onClick={() => handleViewFile(file?.id)}
                          disabled={isViewing}
                        >
                          <VisibilityIcon fontSize="small" />
                        </Button>
                        <Button
                          size="small"
                          onClick={() => handleDownloadFile(file?.id)}
                          disabled={isDownloading}
                        >
                          <DownloadIcon fontSize="small" />
                        </Button>
                        <Button
                          size="small"
                          onClick={() => handleDeleteClick(file)}
                          disabled={isDeleting}
                        >
                          <DeleteIcon fontSize="small" />
                        </Button>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {(award?.status === "unclaimed" ||
                award?.status === "assigned" ||
                award?.status === "rejected") && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleOpenDialog(award)}
                    disabled={isClaiming}
                  >
                    {isClaiming ? "Claiming..." : "Claim Award"}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
        {selectedAward && (
          <ClaimAwardDialog
            open={openDialog}
            onClose={handleCloseDialog}
            onConfirm={handleConfirmClaim}
            award={selectedAward}
          />
        )}
        {openConfirmDelete && (
          <ConfirmationDialog
            open={openConfirmDelete}
            title="Confirm File Deletion"
            message="Are you sure you want to delete this file?"
            onConfirm={() => handleDeleteConfirm()}
            onCancel={() => {
              setOpenConfirmDelete(false);
            }}
            confirmText="Delete"
            cancelText="Cancel"
          />
        )}
      </Box>
    </Box>
  );
};

export default memo(AwardedStudentView);
